import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, Mail, Pencil, Play, Plus, Sparkles, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import {
  triggerLabel,
  useAdminConfig,
  useEmailPreview,
  useEnrollments,
  usePeople,
  useSequencePreview,
  useSequences,
  useTemplates,
  useTriggerMatches,
  useTriggerPreview,
  type EmailPreview,
  type Enrollment,
  type Sequence,
  type Template,
  type TriggerDef,
  type TriggerKind,
} from "../../lib/admin";
import { Collapsible } from "@ark-ui/react/collapsible";
import { Tabs } from "@ark-ui/react/tabs";
import {
  Ago,
  Badge,
  Button,
  Callout,
  Card,
  CardEmpty,
  CardHeader,
  Checkbox,
  Disclosure,
  Empty,
  ErrorState,
  Fact,
  FactList,
  Field,
  IconButton,
  InlineCode,
  Input,
  ListEditor,
  Modal,
  None,
  PageHeader,
  RadioCards,
  SearchInput,
  Segmented,
  SelectField,
  Skeleton,
  TabBar,
  Table,
  TableScroll,
  Textarea,
  notifyError,
  toaster,
  useConfirm,
} from "../../components/ui";
import { EnrollmentStatusBadge, SendingNotice, invalidateAdmin, usePageTitle } from "./components";

export function AdminOutreach() {
  const [tab, setTab] = useState("sequences");
  usePageTitle("Outreach");
  const config = useAdminConfig();
  const templates = useTemplates();
  const qc = useQueryClient();
  const [busy, setBusy] = useState<"kit" | "run" | null>(null);

  const starterKit = async () => {
    if (busy) return;
    setBusy("kit");
    try {
      const r = await api<{ templates: number; sequences: number }>("/admin/starter-kit", { method: "POST" });
      toaster.success({ title: `Added ${r.templates} templates and ${r.sequences} sequences` });
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };

  const runDue = async () => {
    if (busy) return;
    setBusy("run");
    try {
      const r = await api<{ due: number; sent: number; logged: number }>("/admin/outreach/run", { method: "POST" });
      toaster.success({ title: r.due ? `Processed ${r.due} due step${r.due === 1 ? "" : "s"}` : "Nothing due right now" });
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Outreach"
        subtitle={
          config.data &&
          `${config.data.sending ? `Sending as ${config.data.fromName} <${config.data.fromEmail}>` : "Log-only mode"} · ${config.data.sentToday}/${config.data.dailyLimit} sent today · sequences run every 15 minutes`
        }
        actions={
          <>
            {templates.data && !templates.data.length && (
              <Button onClick={starterKit} disabled={busy !== null}>
                <Sparkles size={14} aria-hidden /> {busy === "kit" ? "Adding…" : "Add starter kit"}
              </Button>
            )}
            <Button onClick={runDue} disabled={busy !== null}>
              <Play size={14} aria-hidden /> {busy === "run" ? "Running…" : "Run due steps now"}
            </Button>
          </>
        }
      />
      <div className="mb-4">
        <SendingNotice />
      </div>
      <TabBar
        aria-label="Outreach"
        items={[
          { value: "sequences", label: "Sequences" },
          { value: "templates", label: "Templates" },
        ]}
        value={tab}
        onChange={setTab}
      >
        <Tabs.Content value="sequences" className="mt-4 outline-none">
          <Sequences onStarter={starterKit} />
        </Tabs.Content>
        <Tabs.Content value="templates" className="mt-4 outline-none">
          <Templates onStarter={starterKit} />
        </Tabs.Content>
      </TabBar>
    </>
  );
}

// --- Sequences -----------------------------------------------------------------------------

function Sequences({ onStarter }: { onStarter: () => void }) {
  const q = useSequences();
  const templates = useTemplates();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Sequence | "new" | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<Sequence | null>(null);
  const { confirm, dialog } = useConfirm();
  const tplName = new Map(templates.data?.map((t) => [t.id, t.name]));

  if (q.isError)
    return (
      <Card>
        <ErrorState error={q.error} />
      </Card>
    );
  if (q.isPending) return <Skeleton className="h-40" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setEditing("new")} disabled={!templates.data?.length}>
          <Plus size={14} /> New sequence
        </Button>
      </div>
      {!q.data?.length && (
        <Card>
          <Empty title="No sequences yet">
            A sequence sends a few emails on a schedule and stops the moment someone replies.
            <div className="mt-3">
              <Button onClick={onStarter}>Add the starter kit</Button>
            </div>
          </Empty>
        </Card>
      )}
      {q.data?.map((s) => (
        // One sequence's people open at a time, and they load only while open. The content element
        // stays mounted so the trigger's aria-controls always points at something.
        <Collapsible.Root key={s.id} open={viewing === s.id} onOpenChange={(d) => setViewing(d.open ? s.id : null)}>
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  {s.name} {!s.active && <Badge>Paused</Badge>}
                </span>
              }
              subtitle={
                <>
                  {s.trigger.kind === "manual" ? "Manual" : `Starts itself: ${s.trigger.label}`} · {s.active_count} active · {s.completed_count} completed · {s.replied_count} replied
                  {s.total_count ? ` (${Math.round((s.replied_count / s.total_count) * 100)}% reply rate)` : ""}
                  {s.auto_count ? ` · ${s.auto_count} enrolled automatically` : ""}
                </>
              }
              actions={
                <>
                  <Button size="sm" variant="ghost" onClick={() => setPreviewing(s)} aria-label={`Preview ${s.name}`}>
                    <Eye size={14} aria-hidden /> Preview
                  </Button>
                  <Collapsible.Trigger asChild>
                    <Button size="sm" variant="ghost">
                      {viewing === s.id ? "Hide people" : `People (${s.total_count})`}
                    </Button>
                  </Collapsible.Trigger>
                  <IconButton label={`Edit ${s.name}`} onClick={() => setEditing(s)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    label={`Delete ${s.name}`}
                    tone="danger"
                    onClick={async () => {
                      if (!(await confirm({ title: `Delete ${s.name}?`, description: "Everyone still in it stops getting its emails.", confirmLabel: "Delete" }))) return;
                      await api(`/admin/sequences/${s.id}`, { method: "DELETE" }).catch(notifyError);
                      await invalidateAdmin(qc);
                    }}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </>
              }
            />
            <ol className="flex flex-wrap items-center gap-2 px-4 pb-4 text-[13px]">
              {s.steps.map((st, i) => (
                <li key={i} className="flex items-center gap-2">
                  {i > 0 && (
                    <span className="text-xs text-ink-3">
                      <span aria-hidden>→ </span>then wait {st.delayDays} day{st.delayDays === 1 ? "" : "s"}
                      <span aria-hidden> →</span>
                    </span>
                  )}
                  {i === 0 && st.delayDays > 0 && (
                    <span className="text-xs text-ink-3">
                      wait {st.delayDays} day{st.delayDays === 1 ? "" : "s"}
                      <span aria-hidden> →</span>
                    </span>
                  )}
                  <span className="sr-only">send</span>
                  <span className="rounded-md border border-line bg-surface-2 px-2 py-1 text-ink">{tplName.get(st.templateId) ?? "Missing template"}</span>
                </li>
              ))}
            </ol>
            {s.trigger.kind !== "manual" && <TriggerStatus sequence={s} />}
            <Collapsible.Content>{viewing === s.id && <EnrollmentTable sequenceId={s.id} />}</Collapsible.Content>
          </Card>
        </Collapsible.Root>
      ))}
      {editing && <SequenceModal sequence={editing === "new" ? null : editing} templates={templates.data ?? []} onClose={() => setEditing(null)} />}
      {previewing && <SequencePreviewModal sequence={previewing} onClose={() => setPreviewing(null)} />}
      {dialog}
    </div>
  );
}

/**
 * Under a sequence that starts itself: who its trigger matched before it was switched on (not
 * emailed unless you ask), and who's waiting for the next hourly run.
 */
function TriggerStatus({ sequence: s }: { sequence: Sequence }) {
  const q = useTriggerMatches(s.id, s.active);
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const plural = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

  const enrolEarlier = async (n: number) => {
    const ok = await confirm({
      title: `Also enrol ${plural(n)} who already match?`,
      description: `They matched "${s.trigger.label}" before this sequence started itself. Their first step goes out on the next run, within the daily limit.`,
      confirmLabel: "Enrol",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api<{ enrolled: number; skipped: number }>(`/admin/sequences/${s.id}/enroll-matching`, { method: "POST" });
      toaster.success({ title: `Enrolled ${plural(r.enrolled)}` });
      await invalidateAdmin(qc);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!s.active) {
    return (
      <div className="px-4 pb-4">
        <Callout size="xs">Paused, so the trigger enrols no one. When you resume it, it starts counting again from then.</Callout>
      </div>
    );
  }
  return (
    <div className="px-4 pb-4" aria-busy={q.isPending}>
      {q.isError ? (
        <Callout tone="bad" size="xs" role="alert">
          Couldn't check who matches: {q.error instanceof Error ? q.error.message : String(q.error)}
        </Callout>
      ) : q.isPending ? (
        <Skeleton className="h-9" />
      ) : (
        <Callout
          size="xs"
          action={
            q.data.earlier > 0 && (
              <Button size="sm" onClick={() => enrolEarlier(q.data.earlier)} disabled={busy}>
                {busy ? "Enrolling…" : `Also enrol ${plural(q.data.earlier)}`}
              </Button>
            )
          }
        >
          Checked every hour. {s.auto_count ? `${plural(s.auto_count)} enrolled automatically so far.` : "No one enrolled automatically yet."}{" "}
          {q.data.earlier > 0 && `${plural(q.data.earlier)} matched before it was switched on; they won't be emailed unless you add them. `}
          {q.data.waiting > 0 && `${plural(q.data.waiting)} waiting for the next run or for room under the daily limit.`}
        </Callout>
      )}
      {dialog}
    </div>
  );
}

/** Why someone is in a sequence: who added them, or which trigger and when. */
function EnrollmentReason({ e, triggers }: { e: Enrollment; triggers: TriggerDef[] | undefined }) {
  const who = e.enrolled_by_name || null;
  if (e.source === "manual") return <>{who ? `Added by ${who}` : "Added by hand"}</>;
  const label = e.trigger_kind ? triggerLabel(triggers, e.trigger_kind, e.trigger_days) : "Trigger";
  return (
    <>
      {e.source === "trigger" ? "Started itself" : `Added by ${who ?? "an admin"} from those who already matched`}
      <div className="text-xs text-ink-3">
        {label}
        {e.matched_at && (
          <>
            {" "}
            <span aria-hidden>·</span> matched <Ago t={e.matched_at} />
          </>
        )}
      </div>
    </>
  );
}

function EnrollmentTable({ sequenceId }: { sequenceId: string }) {
  const q = useEnrollments(sequenceId);
  const config = useAdminConfig();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  if (q.isError) return <ErrorState error={q.error} />;
  if (!q.data) return <Skeleton className="m-4 h-16" />;
  if (!q.data.length) return <CardEmpty divided>No one enrolled yet. Add people from People or Plays.</CardEmpty>;
  return (
    <TableScroll label="People in this sequence" divided>
      <Table caption="People in this sequence" className="min-w-[820px]">
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Why they're in it</th>
            <th scope="col">Status</th>
            <th scope="col">Steps sent</th>
            <th scope="col">Next step</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {q.data.map((e) => (
            <tr key={e.id}>
              <td>
                <Link to={`/admin/people/${e.contact_id}`} className="font-medium text-ink hover:underline">
                  {e.contact_name ?? e.contact_email}
                </Link>
                <div className="text-xs text-ink-3">{e.contact_company ?? e.contact_email}</div>
              </td>
              <td className="text-[13px] text-ink-2">
                <EnrollmentReason e={e} triggers={config.data?.triggers} />
                <div className="text-xs text-ink-3">
                  Enrolled <Ago t={e.created_at} />
                </div>
              </td>
              <td>
                <EnrollmentStatusBadge status={e.status} />
                {e.last_error && <div className="mt-0.5 max-w-60 truncate text-[11px] text-bad" title={e.last_error}>{e.last_error}</div>}
              </td>
              <td className="tabular">{e.step_index}</td>
              <td className="text-xs text-ink-2">{e.status === "active" ? <Ago t={e.next_run_at} empty="–" /> : <None />}</td>
              <td className="text-right">
                {e.status === "active" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Stop sequence for ${e.contact_name ?? e.contact_email}`}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Stop the sequence for ${e.contact_name ?? e.contact_email}?`,
                        description: "They get no more emails from it, and it can't be restarted.",
                        confirmLabel: "Stop",
                      });
                      if (!ok) return;
                      await api(`/admin/enrollments/${e.id}/stop`, { method: "POST" }).catch(notifyError);
                      await invalidateAdmin(qc);
                    }}
                  >
                    Stop
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {dialog}
    </TableScroll>
  );
}

function SequenceModal({ sequence, templates, onClose }: { sequence: Sequence | null; templates: Template[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(sequence?.name ?? "");
  const [active, setActive] = useState(sequence?.active ?? true);
  const [steps, setSteps] = useState(sequence?.steps ?? [{ delayDays: 0, templateId: templates[0]?.id ?? "" }]);
  const [trigger, setTrigger] = useState<{ kind: TriggerKind; days: number }>({ kind: sequence?.trigger.kind ?? "manual", days: sequence?.trigger.days ?? 3 });
  const update = (i: number, patch: Partial<(typeof steps)[number]>) => setSteps((s) => s.map((st, j) => (j === i ? { ...st, ...patch } : st)));

  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api(sequence ? `/admin/sequences/${sequence.id}` : "/admin/sequences", { method: sequence ? "PUT" : "POST", json: { name, active, steps, trigger } });
      await invalidateAdmin(qc);
      toaster.success({ title: "Sequence saved" });
      onClose();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={sequence ? "Edit sequence" : "New sequence"} description="Each step waits the given days after the previous one. Replies, unsubscribes and stage changes stop it." wide>
      <div className="space-y-3">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Activation rescue" />
        </Field>
        <div className="space-y-2">
          <span className="text-[13px] font-medium text-ink">Steps</span>
          {/* No reordering: steps never could be, and their waits are relative to the one before. */}
          <ListEditor
            items={steps}
            onChange={setSteps}
            create={() => ({ delayDays: 3, templateId: templates[0]?.id ?? "" })}
            noun="step"
            label="Steps"
            keyOf={(_, i) => i}
            min={1}
            max={10}
            reorder={false}
            render={(st, i, controls) => (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface-2 p-2">
                <span className="w-12 text-xs font-medium text-ink-3">Step {i + 1}</span>
                <span className="text-xs text-ink-2">wait</span>
                <Input
                  type="number"
                  min={0}
                  max={90}
                  value={st.delayDays}
                  onChange={(e) => update(i, { delayDays: Math.max(0, Math.min(90, Number(e.target.value) || 0)) })}
                  aria-label={`Step ${i + 1} delay in days`}
                  className="h-8 w-16 text-[13px]"
                />
                <span className="text-xs text-ink-2">days, then send</span>
                <SelectField
                  aria-label={`Step ${i + 1} template`}
                  className="min-w-48 flex-1"
                  items={templates.map((t) => ({ value: t.id, label: t.name }))}
                  value={st.templateId}
                  onChange={(v) => update(i, { templateId: v })}
                />
                {controls}
              </div>
            )}
          />
        </div>
        <TriggerPicker value={trigger} onChange={setTrigger} sequence={sequence} />
        <Checkbox checked={active} onCheckedChange={setActive}>
          Active (paused sequences hold their enrollments until resumed)
        </Checkbox>
        <div className="flex justify-end">
          <Button variant="primary" onClick={save} disabled={saving || !name.trim() || steps.some((s) => !s.templateId)}>
            {saving ? "Saving…" : "Save sequence"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** How a sequence starts: by hand, or by itself when someone matches a trigger. */
function TriggerPicker({
  value,
  onChange,
  sequence,
}: {
  value: { kind: TriggerKind; days: number };
  onChange: (v: { kind: TriggerKind; days: number }) => void;
  sequence: Sequence | null;
}) {
  const config = useAdminConfig();
  const defs = config.data?.triggers ?? [];
  const def = defs.find((d) => d.kind === value.kind);
  const days = def?.days ? value.days : null;
  const matching = useTriggerPreview(value.kind, days);
  // Saving an unchanged trigger keeps its clock; anything else starts it from now.
  const unchanged = sequence && sequence.trigger.kind === value.kind && (sequence.trigger.days ?? null) === days;
  const plural = (n: number) => `${n} ${n === 1 ? "person" : "people"}`;

  return (
    <div className="space-y-2">
      <RadioCards
        label="Starts"
        value={value.kind}
        onValueChange={(k) => onChange({ ...value, kind: k as TriggerKind })}
        items={defs.map((d) => ({ value: d.kind, label: d.label, hint: d.help }))}
      />
      {def?.days && (
        <div className="w-32">
          <Field label="Days" hint="Between 1 and 90.">
            <Input type="number" min={1} max={90} value={value.days} onChange={(e) => onChange({ ...value, days: Math.max(1, Math.min(90, Number(e.target.value) || 1)) })} />
          </Field>
        </div>
      )}
      {value.kind !== "manual" && (
        <>
          <Callout size="xs" role="status">
            {matching.isError
              ? "Couldn't check who matches right now."
              : !matching.data
                ? "Checking who matches…"
                : unchanged
                  ? "Checked every hour. People who already matched stay out unless you add them from the sequence card."
                  : `${plural(matching.data.matching)} match now; they won't be emailed, only people who match from now on. You can add them from the sequence card after saving.`}
          </Callout>
          <Callout size="xs" tone="info">
            Metrickle also sends its own onboarding emails (welcome, no data yet, first data, first verdict). Check those before you switch on "Signed up" or "No data", so no one hears
            from us twice.
          </Callout>
        </>
      )}
    </div>
  );
}

/** Every step of a sequence as one person would get it, with the day it goes out. */
function SequencePreviewModal({ sequence, onClose }: { sequence: Sequence; onClose: () => void }) {
  const [contactId, setContactId] = useState<string | null>(null);
  const q = useSequencePreview(sequence.id, contactId);
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Preview ${sequence.name}`} description="Each step as this person would get it, counted in days from when they're enrolled." wide>
      <div className="space-y-4">
        <PreviewAs value={contactId} onChange={setContactId} />
        <div aria-busy={q.isFetching} className="space-y-3">
          {q.isError ? (
            <ErrorState title="Couldn't preview this sequence" error={q.error} />
          ) : !q.data ? (
            <Skeleton className="h-40" />
          ) : (
            q.data.map((st, i) => (
              <section key={i} className="rounded-lg border border-line p-3">
                <h3 className="text-sm font-medium text-ink">
                  Day {st.day} <span aria-hidden>·</span> Step {i + 1}: {st.templateName ?? "Missing template"}
                </h3>
                {st.email ? (
                  <Disclosure summary={`Subject: ${st.email.subject}`} size="xs" className="mt-1" open={i === 0 ? true : undefined}>
                    <EmailPreviewPane preview={st.email} />
                  </Disclosure>
                ) : (
                  <p className="mt-1 text-xs text-bad">This step's template was deleted, so it would fail.</p>
                )}
              </section>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

// --- Preview -------------------------------------------------------------------------------

const SAMPLE = "sample";

/** Who to render a preview for: a contact found by search, or the made-up sample person. */
function PreviewAs({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const people = usePeople();
  const [search, setSearch] = useState("");
  const items = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = people.data ?? [];
    const found = (term ? all.filter((p) => `${p.name ?? ""} ${p.email} ${p.company ?? ""}`.toLowerCase().includes(term)) : all).slice(0, 50);
    // Keep the chosen person listed so the select still shows their name.
    const chosen = value && !found.some((p) => p.id === value) ? all.find((p) => p.id === value) : undefined;
    return [
      { value: SAMPLE, label: "Sample person (Ada, Quillmere)" },
      ...[...(chosen ? [chosen] : []), ...found].map((p) => ({ value: p.id, label: p.name ? `${p.name} · ${p.email}` : p.email })),
    ];
  }, [people.data, search, value]);

  return (
    <div className="grid gap-2 @xl:grid-cols-2">
      <Field label="Find a person" hint={people.isError ? "Couldn't load people; the sample person still works." : "Name, email or company."}>
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. quillmere" />
      </Field>
      <Field label="Preview as">
        <SelectField className="w-full" items={items} value={value ?? SAMPLE} onChange={(v) => onChange(v === SAMPLE ? null : v)} />
      </Field>
    </div>
  );
}

/** One email as it would be sent: who to, the subject, flagged merge fields, then HTML or plain text. */
function EmailPreviewPane({ preview: p }: { preview: EmailPreview }) {
  const [view, setView] = useState("html");
  return (
    <div className="space-y-3">
      {!!p.unknownFields.length && (
        <Callout tone="bad" size="xs">
          Not merge fields, so they're left blank: {withFields(p.unknownFields)}. Check the spelling against the list.
        </Callout>
      )}
      {!!p.emptyFields.length && (
        <Callout tone="warn" size="xs">
          Empty for this person:{" "}
          {p.emptyFields.map((f, i) => (
            <span key={f.key}>
              {i > 0 && "; "}
              <InlineCode>{f.key}</InlineCode> {f.fallback ? `shows "${f.fallback}"` : "shows nothing"}
            </span>
          ))}
          .
        </Callout>
      )}
      <FactList cols={2}>
        <Fact term="To">{p.to}</Fact>
        <Fact term="Subject">{p.subject}</Fact>
      </FactList>
      <Segmented
        aria-label="Email format"
        items={[
          { value: "html", label: "HTML" },
          { value: "text", label: "Plain text" },
        ]}
        value={view}
        onChange={setView}
      />
      {view === "html" ? (
        // sandbox="" runs no scripts and submits no forms: the unsubscribe page can't be confirmed from here.
        <iframe
          sandbox=""
          srcDoc={`<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:16px">${p.html}</body></html>`}
          title={`HTML email preview: ${p.subject}`}
          className="h-80 w-full rounded-lg border border-line bg-page-canvas"
        />
      ) : (
        <pre className="max-h-80 overflow-auto rounded-lg border border-line bg-surface-2 p-3 font-sans text-[13px] whitespace-pre-wrap text-ink">{p.text}</pre>
      )}
    </div>
  );
}

const withFields = (keys: string[]) =>
  keys.map((k, i) => (
    <span key={k}>
      {i > 0 && ", "}
      <InlineCode>{`{{${k}}}`}</InlineCode>
    </span>
  ));

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// --- Templates -----------------------------------------------------------------------------

function Templates({ onStarter }: { onStarter: () => void }) {
  const q = useTemplates();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Template | "new" | null>(null);
  const { confirm, dialog } = useConfirm();
  if (q.isError)
    return (
      <Card>
        <ErrorState error={q.error} />
      </Card>
    );
  if (q.isPending) return <Skeleton className="h-40" />;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setEditing("new")}>
          <Plus size={14} /> New template
        </Button>
      </div>
      {!q.data?.length && (
        <Card>
          <Empty title="No templates yet">
            <Button onClick={onStarter}>Add the starter kit</Button>
          </Empty>
        </Card>
      )}
      <div className="grid gap-4 @2xl:grid-cols-2">
        {q.data?.map((t) => (
          <Card key={t.id} className="flex flex-col">
            <CardHeader
              title={t.name}
              subtitle={<>Subject: {t.subject}</>}
              actions={
                <>
                  <IconButton label={`Edit ${t.name}`} onClick={() => setEditing(t)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    label={`Delete ${t.name}`}
                    tone="danger"
                    onClick={async () => {
                      if (!(await confirm({ title: `Delete the template ${t.name}?`, description: "This can't be undone.", confirmLabel: "Delete" }))) return;
                      await api(`/admin/templates/${t.id}`, { method: "DELETE" }).catch(notifyError);
                      await invalidateAdmin(qc);
                    }}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </>
              }
            />
            <p className="line-clamp-6 px-4 pb-4 text-[13px] whitespace-pre-wrap text-ink-2">{t.body}</p>
          </Card>
        ))}
      </div>
      {editing && <TemplateModal template={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
      {dialog}
    </div>
  );
}

function TemplateModal({ template, onClose }: { template: Template | null; onClose: () => void }) {
  const qc = useQueryClient();
  const config = useAdminConfig();
  const [form, setForm] = useState({ name: template?.name ?? "", subject: template?.subject ?? "", body: template?.body ?? "" });
  const [warn, setWarn] = useState<string[]>([]);
  useEffect(() => setWarn([]), [form.subject, form.body]);
  const [contactId, setContactId] = useState<string | null>(null);
  // Re-render the preview once typing pauses, not on every keystroke.
  const draft = useDebounced({ subject: form.subject, body: form.body }, 400);
  const preview = useEmailPreview(draft.subject.trim() && draft.body.trim() ? { ...draft, contactId } : null);

  const [testing, setTesting] = useState(false);
  const sendTest = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const r = await api<{ to: string }>("/admin/outreach/test", { method: "POST", json: { subject: form.subject, body: form.body, contactId } });
      toaster.success({ title: `Test sent to ${r.to}`, description: "It isn't on anyone's timeline and doesn't count toward today's limit." });
    } catch (err) {
      notifyError(err);
    } finally {
      setTesting(false);
    }
  };

  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await api<{ unknownFields: string[] }>(template ? `/admin/templates/${template.id}` : "/admin/templates", {
        method: template ? "PUT" : "POST",
        json: form,
      });
      await invalidateAdmin(qc);
      if (res.unknownFields.length) {
        setWarn(res.unknownFields);
        toaster.error({ title: "Saved, but check merge fields", description: `Unknown: ${res.unknownFields.join(", ")}` });
      } else {
        toaster.success({ title: "Template saved" });
        onClose();
      }
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={template ? "Edit template" : "New template"} wide>
      <div className="grid gap-4 @2xl:grid-cols-[1fr_200px]">
        <div className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Subject">
            <Input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          </Field>
          <Field label="Body" hint="Plain text. Keep it short and personal; an unsubscribe line is added automatically.">
            <Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={12} className="font-mono leading-relaxed" />
          </Field>
          {!!warn.length && <p className="text-xs text-bad">Unknown merge fields: {warn.join(", ")}</p>}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              onClick={sendTest}
              disabled={testing || !config.data?.sending || !form.subject.trim() || !form.body.trim()}
              aria-describedby={config.data && !config.data.sending ? "test-send-off" : undefined}
            >
              <Mail size={14} aria-hidden /> {testing ? "Sending…" : "Send me a test"}
            </Button>
            <Button variant="primary" onClick={save} disabled={saving || !form.name.trim() || !form.subject.trim() || !form.body.trim()}>
              {saving ? "Saving…" : "Save template"}
            </Button>
          </div>
          {config.data && !config.data.sending && (
            <p id="test-send-off" className="text-right text-xs text-ink-3">
              Test emails need sending set up (<InlineCode>OUTREACH_FROM_EMAIL</InlineCode>). The preview shows exactly what would go out.
            </p>
          )}
        </div>
        <aside className="text-xs">
          <div className="mb-2 font-medium text-ink">Merge fields</div>
          <p className="mb-2 text-ink-3">
            Add a fallback with a pipe: <InlineCode>{"{{first_name | there}}"}</InlineCode>
          </p>
          <ul className="space-y-1.5">
            {config.data?.mergeFields.map((f) => (
              <li key={f.key}>
                <Button variant="link" className="min-h-6 font-mono" onClick={() => setForm((x) => ({ ...x, body: `${x.body}{{${f.key}}}` }))} aria-describedby={`field-${f.key}`}>
                  <span aria-hidden>{`{{${f.key}}}`}</span>
                  <span className="sr-only">Add {f.key.replace(/_/g, " ")} field to the end of the body</span>
                </Button>
                <div id={`field-${f.key}`} className="text-ink-3">
                  {f.description}
                </div>
              </li>
            ))}
          </ul>
        </aside>
      </div>
      <section aria-labelledby="template-preview" className="mt-6 space-y-3 border-t border-line pt-4">
        <h3 id="template-preview" className="text-sm font-medium text-ink">
          Preview
        </h3>
        <p className="text-xs text-ink-3">Exactly what would be sent, unsubscribe line included. It updates as you type; nothing is sent.</p>
        <PreviewAs value={contactId} onChange={setContactId} />
        <div aria-busy={preview.isFetching}>
          {!draft.subject.trim() || !draft.body.trim() ? (
            <p className="text-sm text-ink-3">Add a subject and a body to see the preview.</p>
          ) : preview.isError ? (
            <ErrorState title="Couldn't render the preview" error={preview.error} />
          ) : !preview.data ? (
            <Skeleton className="h-80" />
          ) : (
            <EmailPreviewPane preview={preview.data} />
          )}
        </div>
      </section>
    </Modal>
  );
}
